import React, { useState } from 'react';
import { X, ExternalLink, Car, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ConfiguratorOption {
  id: string;
  name: string;
  url: string;
  icon: React.ReactNode;
  color: string;
}

// SVG Logos für die Automarken
const VolkswagenLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <path d="M20 8h60v24H20z"/>
    <circle cx="30" cy="20" r="6" fill="white"/>
    <circle cx="70" cy="20" r="6" fill="white"/>
    <text x="50" y="25" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">VW</text>
  </svg>
);

const SkodaLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <circle cx="50" cy="20" r="15" fill="#4CAF50"/>
    <path d="M35 15h30v10H35z" fill="white"/>
    <text x="50" y="22" textAnchor="middle" fill="#4CAF50" fontSize="10" fontWeight="bold">ŠKODA</text>
  </svg>
);

const FordLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <ellipse cx="50" cy="20" rx="25" ry="12" fill="#1976D2"/>
    <text x="50" y="24" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">FORD</text>
  </svg>
);

const SeatLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <rect x="20" y="10" width="60" height="20" rx="10" fill="#FFC107"/>
    <text x="50" y="22" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">SEAT</text>
  </svg>
);

const CupraLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <path d="M20 10h60v20H20z" fill="#FF9800"/>
    <text x="50" y="22" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">CUPRA</text>
  </svg>
);

const AudiLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <circle cx="30" cy="20" r="8" fill="#424242"/>
    <circle cx="50" cy="20" r="8" fill="#424242"/>
    <circle cx="70" cy="20" r="8" fill="#424242"/>
    <text x="50" y="35" textAnchor="middle" fill="#424242" fontSize="8" fontWeight="bold">AUDI</text>
  </svg>
);

const JeepLogo = () => (
  <svg viewBox="0 0 100 40" className="w-12 h-12 fill-current">
    <rect x="25" y="8" width="50" height="24" rx="4" fill="#8D6E63"/>
    <text x="50" y="22" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">JEEP</text>
  </svg>
);

const configurators: ConfiguratorOption[] = [
  {
    id: 'volkswagen',
    name: 'Volkswagen',
    url: 'https://www.volkswagen.de/de/konfigurator.html?---=%7B%22configuration-step-navigation-service%22%3A%22%2F%3FconfigStep%3D%257B%2522context%2522%253A%2522mofa-standalone%2522%252C%2522selectedStep%2522%253A%2522carline%2522%257D%22%7D',
    icon: <VolkswagenLogo />,
    color: 'bg-[#add015]'
  },
  {
    id: 'skoda',
    name: 'Škoda',
    url: 'https://cc.skoda-auto.com/deu/de-DE?pagegroup=Website&salesprogram=DEU&type=Car%20configurator&_gl=1*viulcj*_gcl_au*MjEyMzcwOTA0NS4xNzUwNjc5NzQ3',
    icon: <SkodaLogo />,
    color: 'bg-[#add015]'
  },
  {
    id: 'ford',
    name: 'Ford',
    url: 'https://www.ford.de/fahrzeuge?intcmp=Build-Your-Ford',
    icon: <FordLogo />,
    color: 'bg-[#add015]'
  },
  {
    id: 'seat',
    name: 'SEAT',
    url: 'https://www.seat.de/konfigurator/w/model?wf=seat-de-de',
    icon: <SeatLogo />,
    color: 'bg-gray-500'
  },
  {
    id: 'cupra',
    name: 'CUPRA',
    url: 'https://www.cupraofficial.de/konfigurator/w/model?wf=cupra-de',
    icon: <CupraLogo />,
    color: 'bg-gray-500'
  },
  {
    id: 'audi',
    name: 'Audi',
    url: 'https://www.audi.de/de/neuwagen/',
    icon: <AudiLogo />,
    color: 'bg-gray-800'
  },
  {
    id: 'jeep',
    name: 'Jeep',
    url: 'https://www.jeep.de/konfigurator/#/',
    icon: <JeepLogo />,
    color: 'bg-gray-600'
  }
];

const VehicleConfigurator: React.FC = () => {
  const handleConfiguratorClick = (configurator: ConfiguratorOption) => {
    window.open(configurator.url, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header der Unterseite mit nur einem klaren Zurück-Link */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-3 items-center">
            <div className="justify-self-start">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Zurück zum Hub</span>
              </Link>
            </div>
            <div className="justify-self-center">
              <h1 className="text-3xl font-bold text-gray-900">Fahrzeugkonfiguration</h1>
            </div>
            <div className="justify-self-end"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {configurators.map((configurator) => (
            <div
              key={configurator.id}
              onClick={() => handleConfiguratorClick(configurator)}
              className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015] cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${configurator.color} group-hover:opacity-80 transition-opacity`}>
                  <div className="text-white">
                    {configurator.icon}
                  </div>
                </div>
                <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#add015] transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
                {configurator.name}
              </h3>
              <p className="text-gray-600">
                Konfigurieren Sie Ihr {configurator.name} Wunschfahrzeug
              </p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default VehicleConfigurator; 